/** AuthStore — developer session state. Bootstraps from `GET /profile`.
 *  Verification status (`onboarding` / `verified`) drives sidebar variant. */
import {makeAutoObservable, runInAction} from "mobx";

import apiClient from "/utils/apiClient";

export type DeveloperStatus = "onboarding" | "verified" | "blocked";

export interface Developer {
    uuid: string;
    email: string;
    /** Requested new email; becomes `email` once the link sent to it is opened. */
    pending_email?: string | null;
    name: string;
    status: DeveloperStatus;
    kyc_verified_at?: string | null;
    dpa_signed_at?: string | null;
    payment_account_id?: string | null;
    display_name?: string | null;
    country?: string | null;
    website?: string | null;
    avatar_url?: string | null;
    suspended_at?: string | null;
    deleted_at?: string | null;
    payout_threshold_usd?: number;
}

export interface ProfilePatch {
    name?: string;
    email?: string;
    display_name?: string | null;
    country?: string | null;
    website?: string | null;
}

class AuthStoreClass {
    developer: Developer | null = null;
    loading = false;
    bootError: string | null = null;
    /** True once `/profile` has resolved at least once (200 or 401).
     *  Public so RequireAuth can wait for it before redirecting — otherwise
     *  page-refresh races render-vs-bootstrap and pings anyone to /sign-in. */
    booted = false;

    constructor() {
        makeAutoObservable(this);
    }

    get isAuthenticated(): boolean {
        return this.developer !== null;
    }

    get isVerified(): boolean {
        return this.developer?.status === "verified";
    }

    async bootstrap(): Promise<void> {
        if (this.booted) return;
        this.loading = true;
        try {
            const {data} = await apiClient.get<Developer>("/profile");
            runInAction(() => {
                this.developer = data;
                this.bootError = null;
            });
        } catch {
            runInAction(() => { this.developer = null; });
        } finally {
            runInAction(() => {
                this.loading = false;
                this.booted = true;
            });
        }
    }

    async signIn(email: string, password: string): Promise<void> {
        const {data} = await apiClient.post<Developer>("/auth/login", {email, password});
        runInAction(() => {
            this.developer = data;
        });
    }

    async signUp(email: string, password: string, name: string): Promise<void> {
        await apiClient.post("/auth/register", {email, password, name});
    }

    async signOut(): Promise<void> {
        try {
            await apiClient.post("/auth/logout");
        } finally {
            runInAction(() => { this.developer = null; });
        }
    }

    async updateProfile(patch: ProfilePatch): Promise<void> {
        const {data} = await apiClient.put<Developer>("/profile", patch);
        runInAction(() => { this.developer = data; });
    }

    /** Public link from the email-change mail; refreshes the session's developer if it is the same account. */
    async confirmEmailChange(token: string): Promise<Developer> {
        const {data} = await apiClient.post<Developer>(`/profile/email/confirm/${token}`);
        runInAction(() => {
            if (this.developer?.uuid === data.uuid) this.developer = data;
        });
        return data;
    }

    async cancelEmailChange(): Promise<void> {
        const {data} = await apiClient.delete<Developer>("/profile/email/pending");
        runInAction(() => { this.developer = data; });
    }

    async uploadAvatar(file: File): Promise<void> {
        const form = new FormData();
        form.append("file", file, file.name);
        const {data} = await apiClient.post<Developer>("/profile/avatar", form, {
            headers: {"Content-Type": "multipart/form-data"},
        });
        runInAction(() => { this.developer = data; });
    }

    async removeAvatar(): Promise<void> {
        const {data} = await apiClient.delete<Developer>("/profile/avatar");
        runInAction(() => { this.developer = data; });
    }

    async suspend(): Promise<void> {
        const {data} = await apiClient.post<Developer>("/profile/suspend");
        runInAction(() => { this.developer = data; });
    }

    async resume(): Promise<void> {
        const {data} = await apiClient.post<Developer>("/profile/resume");
        runInAction(() => { this.developer = data; });
    }

    async deleteAccount(): Promise<void> {
        await apiClient.delete("/profile");
        runInAction(() => { this.developer = null; });
    }

    async updatePayoutThreshold(usd: number): Promise<void> {
        const {data} = await apiClient.patch<Developer>("/onboarding/payment-account", {
            payout_threshold_usd: usd,
        });
        runInAction(() => { this.developer = data; });
    }
}

const AuthStore = new AuthStoreClass();
export default AuthStore;
